import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

export const initialForm = {
  name: "",
  clientId: "",
  clientName: "",
  address: "",
  status: "planned",
  budget: "",
  startDate: "",
  endDate: "",
  laborCost: "",
  materialCost: "",
  otherCost: "",
  totalHours: "",
  workersCount: "",
  materialsCount: "",
  invoiceCount: "",
  notes: "",
};

export const statusOptions = ["planned", "in_progress", "completed", "paused"];
export const detailTabs = ["overview", "workers", "materials", "invoices", "expenses", "timesheets", "profit"];

function n(value) {
  return Number(value || 0);
}

export function formatMoney(value) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(n(value));
}

export function formatDate(value) {
  if (!value) return "—";
  if (typeof value?.toDate === "function") return value.toDate().toLocaleDateString("de-DE");
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("de-DE");
}

function normalizeProject(docSnap) {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,
    name: data.name || "",
    clientId: data.clientId || "",
    clientName: data.clientName || "",
    address: data.address || "",
    status: data.status || "planned",
    budget: n(data.budget),
    startDate: data.startDate || "",
    endDate: data.endDate || "",
    laborCost: n(data.laborCost),
    materialCost: n(data.materialCost),
    otherCost: n(data.otherCost),
    totalHours: n(data.totalHours),
    workersCount: n(data.workersCount),
    materialsCount: n(data.materialsCount),
    invoiceCount: n(data.invoiceCount),
    notes: data.notes || "",
    createdAt: data.createdAt || "",
    updatedAt: data.updatedAt || "",
  };
}

export function toForm(project) {
  if (!project) return initialForm;
  return {
    name: project.name || "",
    clientId: project.clientId || "",
    clientName: project.clientName || "",
    address: project.address || "",
    status: project.status || "planned",
    budget: project.budget ?? "",
    startDate: project.startDate || "",
    endDate: project.endDate || "",
    laborCost: project.laborCost ?? "",
    materialCost: project.materialCost ?? "",
    otherCost: project.otherCost ?? "",
    totalHours: project.totalHours ?? "",
    workersCount: project.workersCount ?? "",
    materialsCount: project.materialsCount ?? "",
    invoiceCount: project.invoiceCount ?? "",
    notes: project.notes || "",
  };
}

function buildPayload(form) {
  return {
    name: String(form.name || "").trim(),
    clientId: form.clientId || "",
    clientName: String(form.clientName || "").trim(),
    address: String(form.address || "").trim(),
    status: form.status || "planned",
    budget: n(form.budget),
    startDate: form.startDate || "",
    endDate: form.endDate || "",
    laborCost: n(form.laborCost),
    materialCost: n(form.materialCost),
    otherCost: n(form.otherCost),
    totalHours: n(form.totalHours),
    workersCount: n(form.workersCount),
    materialsCount: n(form.materialsCount),
    invoiceCount: n(form.invoiceCount),
    notes: String(form.notes || "").trim(),
    updatedAt: serverTimestamp(),
  };
}

export function statusClasses(status, theme) {
  const map = {
    planned: theme === "dark" ? "bg-slate-800 text-slate-200 border-slate-700" : "bg-slate-100 text-slate-700 border-slate-200",
    in_progress: theme === "dark" ? "bg-blue-500/15 text-blue-300 border-blue-400/20" : "bg-blue-50 text-blue-700 border-blue-200",
    completed: theme === "dark" ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/20" : "bg-emerald-50 text-emerald-700 border-emerald-200",
    paused: theme === "dark" ? "bg-amber-500/15 text-amber-300 border-amber-400/20" : "bg-amber-50 text-amber-700 border-amber-200",
  };
  return map[status] || map.planned;
}

function matchesProject(record, project) {
  const recordId = String(record?.projectId || "").trim();
  const recordName = String(record?.projectName || record?.title || record?.name || "").trim().toLowerCase();
  const projectName = String(project?.name || "").trim().toLowerCase();
  return (recordId && recordId === project?.id) || (recordName && projectName && recordName === projectName);
}

function calcTimesheetHours(entry) {
  if (entry?.hours != null && entry?.hours !== "") return Number(entry.hours || 0);
  const [sh, sm] = String(entry?.startTime || "").split(":").map(Number);
  const [eh, em] = String(entry?.endTime || "").split(":").map(Number);
  if ([sh, sm, eh, em].some(Number.isNaN)) return 0;
  const worked = eh * 60 + em - (sh * 60 + sm) - Number(entry?.breakMinutes || 0);
  return worked > 0 ? Number((worked / 60).toFixed(2)) : 0;
}

async function safeGetDocs(q, fallbackCollectionName) {
  try {
    return await getDocs(q);
  } catch (error) {
    if (!fallbackCollectionName) throw error;
    return getDocs(collection(db, fallbackCollectionName));
  }
}

function dedupeById(items = []) {
  return Array.from(new Map(items.map((item) => [item.id || JSON.stringify(item), item])).values());
}

async function loadRelatedForProject(project) {
  if (!project?.id) {
    return {
      offers: [],
      timesheets: [],
      invoices: [],
      events: [],
      materials: [],
      expenses: [],
    };
  }

  const projectName = String(project.name || "").trim();

  const [offersById, timesheetsById, invoicesById, eventsById, materialsById, expensesById] = await Promise.all([
    safeGetDocs(query(collection(db, "offers"), where("projectId", "==", project.id)), "offers"),
    safeGetDocs(query(collection(db, "timesheets"), where("projectId", "==", project.id)), "timesheets"),
    safeGetDocs(query(collection(db, "invoices"), where("projectId", "==", project.id)), "invoices"),
    safeGetDocs(query(collection(db, "calendar_events"), where("projectId", "==", project.id)), "calendar_events"),
    safeGetDocs(query(collection(db, "materials"), where("projectId", "==", project.id)), "materials"),
    safeGetDocs(query(collection(db, "expenses"), where("projectId", "==", project.id)), "expenses"),
  ]);

  const base = {
    offers: offersById.docs.map((item) => ({ id: item.id, ...item.data() })),
    timesheets: timesheetsById.docs.map((item) => ({ id: item.id, ...item.data() })),
    invoices: invoicesById.docs.map((item) => ({ id: item.id, ...item.data() })),
    events: eventsById.docs.map((item) => ({ id: item.id, ...item.data() })),
    materials: materialsById.docs.map((item) => ({ id: item.id, ...item.data() })),
    expenses: expensesById.docs.map((item) => ({ id: item.id, ...item.data() })),
  };

  const needsNameFallback = [
    base.offers,
    base.timesheets,
    base.invoices,
    base.events,
    base.materials,
    base.expenses,
  ].some((items) => items.length === 0) && Boolean(projectName);

  if (!needsNameFallback) {
    return base;
  }

  const [offersAll, timesheetsAll, invoicesAll, eventsAll, materialsAll, expensesAll] = await Promise.all([
    base.offers.length ? Promise.resolve(null) : safeGetDocs(query(collection(db, "offers"), orderBy("createdAt", "desc")), "offers"),
    base.timesheets.length ? Promise.resolve(null) : safeGetDocs(query(collection(db, "timesheets"), orderBy("createdAt", "desc")), "timesheets"),
    base.invoices.length ? Promise.resolve(null) : safeGetDocs(query(collection(db, "invoices"), orderBy("createdAt", "desc")), "invoices"),
    base.events.length ? Promise.resolve(null) : safeGetDocs(query(collection(db, "calendar_events"), orderBy("createdAt", "desc")), "calendar_events"),
    base.materials.length ? Promise.resolve(null) : safeGetDocs(query(collection(db, "materials"), orderBy("createdAt", "desc")), "materials"),
    base.expenses.length ? Promise.resolve(null) : safeGetDocs(query(collection(db, "expenses"), orderBy("createdAt", "desc")), "expenses"),
  ]);

  const mergeFallback = (existing, snap) => {
    if (!snap) return existing;
    const matched = snap.docs
      .map((item) => ({ id: item.id, ...item.data() }))
      .filter((item) => matchesProject(item, project));
    return dedupeById([...existing, ...matched]);
  };

  return {
    offers: mergeFallback(base.offers, offersAll),
    timesheets: mergeFallback(base.timesheets, timesheetsAll),
    invoices: mergeFallback(base.invoices, invoicesAll),
    events: mergeFallback(base.events, eventsAll),
    materials: mergeFallback(base.materials, materialsAll),
    expenses: mergeFallback(base.expenses, expensesAll),
  };
}

export default function useProjectsData({ text, notify }) {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [related, setRelated] = useState({
    offers: [],
    timesheets: [],
    invoices: [],
    events: [],
    materials: [],
    expenses: [],
  });

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const snap = await safeGetDocs(
        query(collection(db, "projects"), orderBy("createdAt", "desc")),
        "projects"
      );
      const rows = snap.docs.map(normalizeProject);
      setProjects(rows);
      setSelectedProjectId((current) => current || rows[0]?.id || "");
    } catch (error) {
      console.error(error);
      notify.error(text.loadError);
    } finally {
      setLoadingProjects(false);
    }
  }, [notify, text.loadError]);

  const loadClients = useCallback(async () => {
    try {
      const snap = await safeGetDocs(
        query(collection(db, "invoice_clients"), orderBy("createdAt", "desc")),
        "invoice_clients"
      );
      setClients(
        snap.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }))
      );
    } catch (error) {
      console.warn("Clients load skipped:", error);
      setClients([]);
    }
  }, []);

  useEffect(() => {
    loadProjects();
    loadClients();
  }, [loadProjects, loadClients]);

  const filteredProjects = useMemo(() => {
    const needle = String(search || "").toLowerCase().trim();
    return projects.filter((project) => {
      const matchesStatus = statusFilter === "all" ? true : project.status === statusFilter;
      const haystack = [project.name, project.clientName, project.address].join(" ").toLowerCase();
      const matchesSearch = !needle || haystack.includes(needle);
      return matchesStatus && matchesSearch;
    });
  }, [projects, search, statusFilter]);

  const selectedProject = useMemo(
    () => filteredProjects.find((item) => item.id === selectedProjectId) || projects.find((item) => item.id === selectedProjectId) || filteredProjects[0] || null,
    [filteredProjects, projects, selectedProjectId]
  );

  useEffect(() => {
    if (selectedProject?.id && selectedProject.id !== selectedProjectId) {
      setSelectedProjectId(selectedProject.id);
    }
  }, [selectedProject, selectedProjectId]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!selectedProject?.id) {
        setRelated({
          offers: [],
          timesheets: [],
          invoices: [],
          events: [],
          materials: [],
          expenses: [],
        });
        return;
      }

      setLoadingDetails(true);
      try {
        const data = await loadRelatedForProject(selectedProject);
        if (!cancelled) setRelated(data);
      } catch (error) {
        console.warn("Related data load skipped:", error);
        if (!cancelled) {
          setRelated({
            offers: [],
            timesheets: [],
            invoices: [],
            events: [],
            materials: [],
            expenses: [],
          });
        }
      } finally {
        if (!cancelled) setLoadingDetails(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [selectedProject]);

  const projectDetails = useMemo(() => {
    if (!selectedProject) return null;

    const relatedTimesheets = related.timesheets.map((item) => {
      const hours = calcTimesheetHours(item);
      const rate = Number(item?.hourlyRate || item?.rate || 0);
      return {
        ...item,
        hours,
        cost: Number((hours * rate).toFixed(2)),
      };
    });

    const relatedInvoices = dedupeById(
      [...related.invoices].filter((item) => {
        const byProject = matchesProject(item, selectedProject);
        const byClient = selectedProject.clientId && item?.clientId === selectedProject.clientId;
        return byProject || byClient;
      })
    );

    const approvedOfferValue = related.offers
      .filter((item) => item.status === "approved")
      .reduce((sum, item) => sum + Number(item.total || 0), 0);
    const trackedHours = relatedTimesheets.reduce((sum, item) => sum + Number(item.hours || 0), 0);
    const laborLiveCost = relatedTimesheets.reduce((sum, item) => sum + Number(item.cost || 0), 0);
    const invoiceTotal = relatedInvoices.reduce((sum, item) => sum + Number(item.total || item.grandTotal || item.amount || 0), 0);
    const materialTotal = related.materials.reduce(
      (sum, item) =>
        sum + Number(item.total || item.amount || Number(item.quantity || 0) * Number(item.price || item.unitPrice || 0)),
      0
    );
    const expenseTotal = related.expenses.reduce((sum, item) => sum + Number(item.amount || item.total || 0), 0);

    return {
      offers: related.offers,
      timesheets: relatedTimesheets,
      invoices: relatedInvoices,
      events: related.events,
      materials: related.materials,
      expenses: related.expenses,
      approvedOfferValue,
      trackedHours,
      laborLiveCost,
      invoiceTotal,
      materialTotal,
      expenseTotal,
    };
  }, [related, selectedProject]);

  const stats = useMemo(() => {
    const totalProjects = projects.length;
    const activeProjects = projects.filter((item) => item.status === "in_progress").length;
    const plannedProjects = projects.filter((item) => item.status === "planned").length;
    const totalProfit = projects.reduce(
      (sum, item) => sum + (item.budget - item.laborCost - item.materialCost - item.otherCost),
      0
    );
    return { totalProjects, activeProjects, plannedProjects, totalProfit };
  }, [projects]);

  const openCreate = useCallback(() => {
    setEditingProject(null);
    setForm(initialForm);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((project) => {
    setEditingProject(project);
    setForm(toForm(project));
    setModalOpen(true);
  }, []);

  const handleSave = useCallback(
    async (e) => {
      e.preventDefault();
      if (!String(form.name || "").trim()) {
        notify.error(text.requiredName);
        return;
      }

      try {
        const payload = buildPayload(form);
        if (editingProject?.id) {
          await updateDoc(doc(db, "projects", editingProject.id), payload);
          notify.success(text.projectUpdated);
        } else {
          await addDoc(collection(db, "projects"), {
            ...payload,
            createdAt: serverTimestamp(),
          });
          notify.success(text.projectSaved);
        }
        setModalOpen(false);
        setEditingProject(null);
        setForm(initialForm);
        await loadProjects();
      } catch (error) {
        console.error(error);
        notify.error(text.saveError);
      }
    },
    [editingProject?.id, form, loadProjects, notify, text]
  );

  const handleDelete = useCallback(
    async (project) => {
      if (!window.confirm(text.confirmDelete)) return;
      try {
        await deleteDoc(doc(db, "projects", project.id));
        notify.success(text.projectDeleted);
        if (selectedProjectId === project.id) setSelectedProjectId("");
        await loadProjects();
      } catch (error) {
        console.error(error);
        notify.error(text.saveError);
      }
    },
    [loadProjects, notify, selectedProjectId, text]
  );

  const detailProfit = selectedProject
    ? (selectedProject.budget || projectDetails?.approvedOfferValue || 0) -
      ((projectDetails?.laborLiveCost || selectedProject.laborCost || 0) +
        (projectDetails?.materialTotal || selectedProject.materialCost || 0) +
        (projectDetails?.expenseTotal || selectedProject.otherCost || 0))
    : 0;

  return {
    projects,
    clients,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    loadingProjects,
    loadingDetails,
    modalOpen,
    setModalOpen,
    editingProject,
    form,
    setForm,
    selectedProjectId,
    setSelectedProjectId,
    activeTab,
    setActiveTab,
    filteredProjects,
    selectedProject,
    projectDetails,
    stats,
    detailProfit,
    openCreate,
    openEdit,
    handleSave,
    handleDelete,
  };
}
